import React, {useRef, useState, useEffect} from "react";
import {TextArea, Label, Flag, Modal} from "semantic-ui-react";
import {IDataTwoLanguageInput} from "../../VirtualizedTableInterfaces";
import PreviewScreen from "./PreviewScreen";
import TranslateIconButton from "./TranslateIconButton";
import {handleBackToEdit, handleConfirm, useGenericPreviewHandler} from "./AWSTranslatePreviewHandler";
import HATEOASClient from "src/services/HATEOASClient";
import {OptionAWSTranslateDto, ResourceListOfOptionDto} from "src/services/Client";

interface ModalShortTextAreaProps {
    value: IDataTwoLanguageInput | null;
    focusKey: string;
    readOnly: boolean;
    onChange: (key: string, value: string) => void;
    dialogHeader?: {
        dialogTitle: string;
        editingColumn: string;
        optionType: string;
        optionCode: string;
        pdipText: string;
        previewAiHeader?: string
        previewAiSubHeader?: string;
        previewAiOriginal?: string;
        previewAiPreview?: string;
        previewAiConfirm?: string;
        previewAiCancel?: string;
        previewAiConfimChange?:string;
        previewAiGrammarSubHeader?: string;
        tooltipAiTranslate?: string;
        tooltipAiGrammar?: string;
    };
    closeOnDimmerClick: (value: boolean) => void;
    isNewGrid?: boolean;
    client?: HATEOASClient;
    postOptions?: ResourceListOfOptionDto;
    apiMethod?: (client: any, postOptions: any) => {execute: (payload: any) => Promise<any>};
    grammarApiMethod?: (client: any, postOptions: any) => {execute: (payload: any) => Promise<any>};
}

const ModalShortTextArea: React.FC<ModalShortTextAreaProps> = (props) => {
    const refInitialFocus = useRef<any>(null);
    const [step, setStep] = useState<"edit" | "preview">("edit");
    const [reviewChecked, setReviewChecked] = useState(false);
    const {
        previewAiHeader = undefined,
        previewAiSubHeader = undefined,
        previewAiOriginal = undefined,
        previewAiPreview = undefined,
        previewAiConfirm = undefined,
        previewAiCancel = undefined,
        previewAiConfimChange = undefined,
        previewAiGrammarSubHeader = undefined,
        tooltipAiTranslate = undefined,
        tooltipAiGrammar = undefined
    } = props.dialogHeader || {};

    const {
    loading: translateLoading,
    error: translateError,
    apiResult: translateResult,
    requestPreview: requestPreview
    } = useGenericPreviewHandler(props);

    const getGbKey = () => {
        if (!props.value) return "1";
        return Object.keys(props.value).find(key => props.value![key].flag === "gb") || "1";
    };

    const handleMouseRelease = (e: any) => {
        props.closeOnDimmerClick(true);
    };

    const [activeGrammarKey, setActiveGrammarKey] = useState<string | null>(null);
    const {
      loading: grammarLoading,
      error: grammarError,
      apiResult: grammarResult,
      requestPreview: requestGrammarPreview,
    } = useGenericPreviewHandler(props);

    const handleGrammarCheck = (key: string) => {
      const { client, postOptions, grammarApiMethod, value } = props;

      if (client && postOptions && grammarApiMethod && value) {
        setActiveGrammarKey(key);
        const grammarPayload: OptionAWSTranslateDto = {
          text: value[key].value || "",
        };

        requestGrammarPreview(
          value,
          (payload) => grammarApiMethod(client, postOptions).execute(payload),
          (res) => {
            setStep("preview");
            setReviewChecked(false);
            setTimeout(
                () => {
                    const preview = document.querySelector(".modal-preview-focus") as HTMLElement | null;
                    if (preview) preview.focus();
                },
                50
            );
          },
          grammarPayload
        );
      }
    };

    useEffect(
        () => {
            if (refInitialFocus.current && props.value && props.focusKey) {
                try {
                    const textLength = props.value[props.focusKey].value.length;
                    refInitialFocus.current.ref.setSelectionRange(textLength, textLength);
                    refInitialFocus.current.focus();
                } catch (e) {
                    console.error("Error setting focus in QuillEditor:", e);
                }
            }
            document.addEventListener("click", handleMouseRelease);
            return () => {
                document.removeEventListener("click", handleMouseRelease);
            };
        },
        []
    );

    const handleRequestPreview = () => {
        if (props.client && props.postOptions && props.apiMethod) {
            requestPreview(
                props.value,
                (payload) => props.apiMethod!(props.client, props.postOptions).execute(payload),
                () => {
                    setStep("preview");
                    setReviewChecked(false);
                    setTimeout(
                        () => {
                            const preview = document.querySelector(".modal-preview-focus") as HTMLElement | null;
                            if (preview) preview.focus();
                        },
                        50
                    );
                }
            );
        }
    };

    const renderEditScreen = () => {
        const {dialogHeader, value, readOnly, focusKey, onChange} = props;
        return (
            <>
                {dialogHeader && (
                    <Modal.Header>
                        <div className="display--flex justify-content--space-between">
                            <span>
                                {dialogHeader.dialogTitle + " - "}
                                <span style={{color: "#950014"}}>{dialogHeader.optionCode}</span>
                                {" - "}
                                <span style={{color: "#950014"}}>{dialogHeader.optionType}</span>
                            </span>
                            <span>{dialogHeader.editingColumn}</span>
                        </div>
                        <div className="display--flex justify-content--space-between">
                            <span>{dialogHeader.pdipText}</span>
                        </div>
                    </Modal.Header>
                )}
                <Modal.Content className='modal-text-area-content-short-text'>
                    {value && Object.keys(value).map(key =>
                    (
                        <div
                            key={'popup-textarea-' + key}
                            className='modal-text-area-tile'
                            onMouseDown={() => props.closeOnDimmerClick(false)}
                        >
                            <div className="modal-text-area-header">
                                <Label basic={true} className="flag-label">
                                    <Flag name={value[key].flag} />
                                </Label>
                                {value[key].flag === "de" && !readOnly ? (
                                    <div style={{ display: "flex" }}>
                                        <TranslateIconButton
                                            type="translate"
                                            loading={translateLoading}
                                            value={value[key].value}
                                            onClick={handleRequestPreview}
                                            disabled={
                                                grammarLoading ||
                                                !value[key].value ||
                                                value[key].value.trim() === "" ||
                                                value[key].value === "<p><br></p>"
                                            }
                                            tooltipMessage={tooltipAiTranslate}
                                        />
                                    </div>
                                ) : null}
                                {(value[key].flag === "de" || value[key].flag === "gb") && !readOnly && (
                                    <TranslateIconButton
                                        type="grammar"
                                        loading={grammarLoading && activeGrammarKey === key}
                                        value={value[key].value}
                                        onClick={() => handleGrammarCheck(key)}
                                        disabled={grammarLoading || translateLoading || !value[key].value || value[key].value.trim() === ""}
                                        tooltipMessage={tooltipAiGrammar}
                                    />
                                )}
                            </div>

                            <TextArea
                                key={key}
                                className="modal-text-area-textarea"
                                value={value[key].value || ""}
                                onChange={(e) => onChange(key, e.currentTarget.value)}
                                disabled={readOnly}
                                ref={!readOnly && focusKey === key ? refInitialFocus : undefined}
                            />
                        </div>
                    )
                    )}
                </Modal.Content>
            </>
        );
    };

    const isPreviewGrammar = activeGrammarKey !== null;
    return step === "edit" ? (
        renderEditScreen()
    ) : (
        <PreviewScreen
            value={props.value}
            apiResult={isPreviewGrammar ? grammarResult : translateResult}
            loading={isPreviewGrammar ? grammarLoading : translateLoading}
            error={isPreviewGrammar ? grammarError : translateError}
            reviewChecked={reviewChecked}
            isNewGrid={props.isNewGrid}
            handleRequestPreview={handleRequestPreview}
            grammarKey={activeGrammarKey}
            onBack={() => {
                setActiveGrammarKey(null);
                handleBackToEdit(setStep, setReviewChecked);
            }}
            previewDialogHeaders={{
                dialogheader: previewAiHeader,
                subHeader: previewAiSubHeader,
                originalLabel: previewAiOriginal,
                previewLabel: previewAiPreview,
                confirmLabel: previewAiConfirm,
                cancelLabel: previewAiCancel,
                confirmChangeLabel: previewAiConfimChange,
                grammarSubHeader: previewAiGrammarSubHeader
            }}
            onConfirm={(previewText) => {
                const targetKey = activeGrammarKey !== null ? activeGrammarKey : getGbKey();
                handleConfirm(
                    previewText,
                    props.onChange,
                    setStep,
                    setReviewChecked,
                    {
                        aiGeneratedShorttext: true,
                    },
                    targetKey
                );
                setActiveGrammarKey(null);
            }
            }
            onCheckReview={setReviewChecked}
        />
    );
};

export default ModalShortTextArea;



// WEBPACK FOOTER //
// ./src/components/Table/components/twoLanguage/ModalShortTextArea.tsx